import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericModal from "@ui-components/Modal/GenericModal";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { ReactComponent as IconCopy } from "@assets/Icons/IconCopy.svg";
import { createApiKey } from "./requests/request";
import { ApiKeyCreated, ProductScope } from "./types";
import { formatDate } from "./apiKeysData";
import HowToConnect from "./HowToConnect";

/**
 * How long a new key lives. Ninety days is the backend default and the
 * same TTL the customer-facing API tokens use; the shorter options exist
 * because a key minted for a demo or a one-off script should not outlive
 * it. There is deliberately no "never" — that is what the legacy keys
 * already are, and the reason expiry was added.
 */
const TTL_OPTIONS = [
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 180, label: "180 days" },
  { value: 365, label: "1 year" }
];

const DEFAULT_TTL_DAYS = 90;

/**
 * What the token may read. The backend rejects an empty set, so the form
 * starts with neither selected and the submit stays disabled until one is
 * picked — a default here would silently grant something nobody chose.
 */
const PRODUCT_OPTIONS: { value: ProductScope; label: string }[] = [
  { value: "kontroledge", label: "Kontrol Edge" },
  { value: "reflector", label: "Reflector" }
];

interface CreateKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** Tenant the key is issued for — the lens the list is currently using. */
  tenantId?: number;
}

const CreateKeyModal: React.FC<CreateKeyModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  tenantId,
}) => {
  const api = useAxiosAuth0();
  const { isAuthenticated } = useAuth0();

  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<ProductScope[]>([]);
  const [subtree, setSubtree] = useState(false);
  const [ttlDays, setTtlDays] = useState<number>(DEFAULT_TTL_DAYS);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [issued, setIssued] = useState<ApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setName("");
    setScopes([]);
    setSubtree(false);
    setTtlDays(DEFAULT_TTL_DAYS);
    setSubmitting(false);
    setErrorMsg(null);
    setIssued(null);
    setCopied(false);
  };

  const handleClose = () => {
    if (issued) onCreated();
    reset();
    onClose();
  };

  const toggleScope = (scope: ProductScope) =>
    setScopes((current) =>
      current.includes(scope)
        ? current.filter((s) => s !== scope)
        : [...current, scope]
    );

  const canSubmit = !!name.trim() && scopes.length > 0;

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!isAuthenticated || !canSubmit) return;

    setSubmitting(true);
    setErrorMsg(null);
    try {
      const res = await createApiKey(
        api,
        {
          name: trimmed,
          productScopes: scopes,
          // Only sent when asked for. The backend refuses `subtree` on a
          // customer-level tenant, and surfaces that as the error below
          // rather than the form guessing the tenant's level.
          ...(subtree ? { accessScope: "subtree" as const } : {}),
          expiresInDays: ttlDays
        },
        tenantId
      );
      setIssued(res.data.data);
    } catch (err: unknown) {
      const fallback = "Could not create key. Try again.";
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || fallback;
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const footer = issued ? (
    <div className="flex justify-end">
      <GenericButton label="Done" variant="brand" onClick={handleClose} />
    </div>
  ) : (
    <div className="flex justify-end gap-2">
      <GenericButton
        label="Cancel"
        variant="secondary"
        onClick={handleClose}
        disabled={submitting}
      />
      <GenericButton
        label={submitting ? "Generating…" : "Generate Key"}
        variant="brand"
        onClick={handleSubmit}
        disabled={submitting || !canSubmit}
      />
    </div>
  );

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleClose}
      title={issued ? "API key created" : "Generate API key"}
      footer={footer}
      size={issued ? "lg" : "md"}
    >
      {issued ? (
        <div className="space-y-4">
          <div className="rounded-md border border-semantic-border-warning-primary bg-semantic-bg-warning-secondary p-3 text-xs text-semantic-text-warning">
            Save this key now. It will not be shown again. The snippets below
            already include your key — copy whichever client you use.
          </div>
          <div className="rounded-md border border-semantic-border-secondary bg-semantic-bg-secondary p-3">
            <div className="flex items-start gap-2">
              <code className="break-all text-xs text-semantic-text-primary flex-1">
                {issued.token}
              </code>
              <button
                type="button"
                onClick={handleCopy}
                className="shrink-0 p-1 rounded hover:bg-semantic-state-active transition-colors"
                title="Copy to clipboard"
                aria-label="Copy key"
              >
                <IconCopy width={16} height={16} />
              </button>
            </div>
          </div>
          {copied && (
            <p className="text-xs text-semantic-text-success">Copied.</p>
          )}
          <div className="text-xs text-semantic-text-secondary space-y-1">
            <div>
              <span className="font-medium">Name:</span> {issued.name}
            </div>
            <div>
              <span className="font-medium">Token:</span>{" "}
              {issued.displayPrefix}…{issued.displaySuffix}
            </div>
            <div>
              <span className="font-medium">Access:</span>{" "}
              {issued.productScopes.join(", ")}
              {issued.accessScope === "subtree" && " · tenant + customers"}
            </div>
            <div>
              <span className="font-medium">Expires:</span>{" "}
              {formatDate(issued.expiresAt)}
            </div>
          </div>
          <div className="pt-2 border-t border-semantic-border-secondary">
            <h3 className="text-sm font-semibold text-semantic-text-primary mb-2">
              Quick start
            </h3>
            <HowToConnect rawKey={issued.token} />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-semantic-text-secondary">
            A key grants read-only access to your data over HTTP. It sees the
            same tenants you do — no more.
          </p>
          <GenericInput
            label="Name"
            placeholder="e.g. Claude desktop, CISCO demo"
            value={name}
            onChange={setName}
            fullWidth
            required
          />
          <div>
            <span className="block text-sm text-semantic-text-primary mb-2">
              Can read
            </span>
            <div className="flex flex-wrap gap-2">
              {PRODUCT_OPTIONS.map((option) => {
                const selected = scopes.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleScope(option.value)}
                    aria-pressed={selected}
                    className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                      selected
                        ? "border-semantic-border-brand bg-semantic-bg-brand-secondary text-semantic-text-brand"
                        : "border-semantic-border-secondary text-semantic-text-secondary hover:bg-semantic-state-active"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/*
            Offered to everyone rather than hidden behind a tenant-level
            check the client would have to duplicate. A customer-level
            tenant that asks for it gets the backend's 403 in the error
            slot below, which is one source of truth instead of two that
            can disagree.
          */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={subtree}
              onChange={(e) => setSubtree(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-sm text-semantic-text-secondary">
              Also read this tenant&apos;s customers
              <span className="block text-xs">
                Partner accounts only.
              </span>
            </span>
          </label>

          <div>
            <span className="block text-sm text-semantic-text-primary mb-2">
              Expires after
            </span>
            <div className="flex flex-wrap gap-2">
              {TTL_OPTIONS.map((option) => {
                const selected = option.value === ttlDays;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setTtlDays(option.value)}
                    aria-pressed={selected}
                    className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                      selected
                        ? "border-semantic-border-brand bg-semantic-bg-brand-secondary text-semantic-text-brand"
                        : "border-semantic-border-secondary text-semantic-text-secondary hover:bg-semantic-state-active"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
          {errorMsg && (
            <p className="text-xs text-semantic-text-danger">{errorMsg}</p>
          )}
        </div>
      )}
    </GenericModal>
  );
};

export default CreateKeyModal;
